/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Drafts';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useCallback, useEffect, useState } from 'react';

import { MemberEmailForm } from 'components';
import ModalTestEmail from 'routes/dashboard/components/MemberNotifications/ModalTestEmail';
import { useAddMemberNotificationMutation, useCreateEmailCarbonCopyMutation } from 'store/api/version-api.slice';
import { getParameterByName } from 'utils';

import NavigationArrows from '../components/NavigationArrows';

import EmailsList from './components/EmailsList';
import { DEFAULT_EMAIL_FORM_VALUES } from './constants';
import { isMissingData } from './helpers';
import { styles } from './styles/memberNotifications.styles';

const MemberEmails = ({ handleNext, handlePrev, isNavigating }) => {
	const [emailFormValues, setEmailFormValues] = useState(DEFAULT_EMAIL_FORM_VALUES);
	const [toggleTestEmail, setToggleTestEmail] = useState(false);

	const [addMemberNotification, { isLoading: isAddLoading }] = useAddMemberNotificationMutation();
	const [createEmailCC, { isLoading: isCreateLoading }] = useCreateEmailCarbonCopyMutation();

	const handleAddEmail = async () => {
		const { memberemailformcc_set = [], ...rest } = emailFormValues;
		const response = await addMemberNotification(rest);

		const createPromises = memberemailformcc_set.map(cc => {
			return createEmailCC({
				...cc,
				member_email_form: response.data?.id
			});
		});

		Promise.allSettled(createPromises);
	};

	const handleFormValueChange = useCallback((prop, value) => {
		setEmailFormValues(previousState => ({
			...previousState,
			[prop]: value
		}));
	}, []);

	const handleToggleTestEmail = () => {
		setToggleTestEmail(prev => !prev);
	};

	useEffect(() => {
		document.title = 'Member Center - Onboarding - Member Emails';

		window._hsq = window._hsq || [];
		const { _hsq } = window;

		_hsq.push(['setPath', '/onboarding-member-emails']);
		_hsq.push(['trackPageView']);
		_hsq.push([
			'identify',
			{
				email: getParameterByName('email'),
				onboarding_stage: 'member_emails'
			}
		]);
	}, []);

	const { email_type, email_body, frequency, email_subject, memberemailformcc_set } = emailFormValues;

	const isLoading = isCreateLoading || isAddLoading;

	return (
		<Grid container spacing={2} sx={styles.layout}>
			<Grid item sm={6} xs={12}>
				<Paper sx={styles.addEmailSection}>
					<MemberEmailForm
						email_type={email_type}
						email_body={email_body}
						frequency={frequency}
						email_subject={email_subject}
						memberemailformcc_set={memberemailformcc_set}
						handleChange={handleFormValueChange}
					/>
					<Box display="flex" alignItems="center" marginTop="16px" flexWrap="wrap">
						<Button
							sx={styles.btnTestEmail}
							onClick={handleToggleTestEmail}
							color="primary"
							disabled={isLoading || isMissingData(emailFormValues)}
						>
							<EmailIcon sx={styles.iconOffset} /> Test Email
						</Button>
						<Button
							variant="contained"
							sx={styles.addEmailButton}
							onClick={handleAddEmail}
							disabled={isLoading || isMissingData(emailFormValues)}
						>
							{isLoading ? (
								<CircularProgress size={21} sx={styles.iconOffset} />
							) : (
								<AddIcon sx={styles.iconOffset} />
							)}
							Add Email Notification
						</Button>
					</Box>
					{toggleTestEmail && (
						<ModalTestEmail
							handleClose={handleToggleTestEmail}
							data={emailFormValues}
							wrapperClassname={styles.positioning}
						/>
					)}
				</Paper>
			</Grid>
			<EmailsList />
			<NavigationArrows
				handleNext={handleNext}
				handlePrev={handlePrev}
				isNextDisabled={isNavigating || isLoading}
				isBackDisabled={isNavigating || isLoading}
				isNavigating={isNavigating}
			/>
		</Grid>
	);
};
export default MemberEmails;
